/**
 * Suggestions, arranged for a reader: grouped by the document they sit on
 * (the library's own first), filtered the way the page filters. Pure, so the
 * arrangement is pinned by tests.
 *
 * @module components/regulation/policy/suggestionsView
 */
import { isOpenSuggestionStatus } from "@repo/shared/reviewCounts";

import type { Recommendation, RecommendationKind } from "../../../state/governance";

export const SUGGESTION_STATUS_PILL: Record<Recommendation["status"], { label: string; className: string }> = {
  open: { label: "Proposed", className: "bg-primary/10 text-primary" },
  // The retired "accepted" stage: undecided, waiting in To review like a
  // proposal, and only marked as someone's work in progress.
  accepted: { label: "Marked in progress", className: "bg-primary/10 text-primary" },
  applied: {
    label: "Applied",
    className: "bg-emerald-600/10 text-emerald-600 dark:text-emerald-300/90",
  },
  dismissed: { label: "Rejected", className: "bg-muted text-muted-foreground" },
};


export function suggestionStatusPill(status: string): { label: string; className: string } {
  return Object.hasOwn(SUGGESTION_STATUS_PILL, status)
    ? SUGGESTION_STATUS_PILL[status as Recommendation["status"]]
    : { label: "Unknown", className: "bg-muted text-muted-foreground" };
}

export const KIND_LABELS: Record<RecommendationKind, string> = {
  rename: "Title",
  review_cadence: "Review cadence",
  owner: "Owner",
  department: "Department",
  jurisdictions: "Jurisdictions",
  reclassify: "Shelf",
  control_mapping: "Control link",
  control_amendment: "Control amendment",
  missing_document: "Missing document",
  framework: "Framework",
  // Named for the surface it changes — Settings → Company profile — rather
  // than for the column behind it, so the label says where to go and look.
  org_context: "Company profile",
};

/** Still waiting on a decision — the shared rule every count and queue uses. */
export const isOpenSuggestion = (item: Pick<Recommendation, "status">): boolean =>
  isOpenSuggestionStatus(item.status);

export interface SuggestionFilters {
  /** "open" is To review: proposals and those marked in progress alike. */
  readonly status?: "all" | "open" | "decided";
  readonly query: string;
  readonly kind: RecommendationKind | "all";
  readonly severity: string;
}

export const EMPTY_SUGGESTION_FILTERS: SuggestionFilters = { query: "", kind: "all", severity: "all" };

export function filterSuggestions(
  items: ReadonlyArray<Recommendation>,
  filters: SuggestionFilters,
  documentNames?: ReadonlyMap<string, string>,
): Recommendation[] {
  const needle = filters.query.trim().toLowerCase();
  return items.filter(
    (item) =>
      (filters.status === undefined || filters.status === "all" ||
        (filters.status === "decided" ? !isOpenSuggestion(item) : isOpenSuggestion(item))) &&
      (filters.kind === "all" || item.kind === filters.kind) &&
      (filters.severity === "all" || (item.severity ?? "none") === filters.severity) &&
      (needle.length === 0 ||
        item.title.toLowerCase().includes(needle) ||
        item.rationale.toLowerCase().includes(needle) ||
        (item.policyName ?? "").toLowerCase().includes(needle) ||
        (documentNames?.get(item.policyId ?? "") ?? "").toLowerCase().includes(needle)),
  );
}

export interface SuggestionGroup {
  /** Stable per group, including the two that have no document. */
  readonly key: string;
  /** Null where there is no document page to link to. */
  readonly policyId: string | null;
  readonly policyName: string;
  readonly items: ReadonlyArray<Recommendation>;
}

/**
 * Which group a suggestion belongs to. Two kinds carry no document and they
 * are not the same group: a missing document is a hole in the library, while a
 * company-profile change is not about the library at all. Folding them
 * together filed "add what the firm does to the company profile" under "The
 * library", which is the wrong place to go and look.
 */
const groupOf = (
  item: Recommendation,
): { key: string; policyId: string | null; policyName: string } =>
  item.kind === "org_context"
    ? { key: "organisation", policyId: null, policyName: "The organisation" }
    : item.policyId === null
      ? { key: "library", policyId: null, policyName: "The library" }
      : { key: item.policyId, policyId: item.policyId, policyName: item.policyName ?? "The library" };

/** The organisation first, then the library, then documents. */
const groupRank = (group: { readonly key: string }): number =>
  group.key === "organisation" ? 0 : group.key === "library" ? 1 : 2;

/**
 * Group by what a suggestion is about: the organisation, the library, then
 * each document by how much it has waiting (most first), ties by name. Within
 * a group, newest first as the API returns them.
 */
export function groupByDocument(items: ReadonlyArray<Recommendation>): SuggestionGroup[] {
  // The group's own items list stays mutable while it fills; the returned type
  // is the read-only one, which a mutable array satisfies without a cast.
  const groups = new Map<string, Omit<SuggestionGroup, "items"> & { items: Recommendation[] }>();
  for (const item of items) {
    const identity = groupOf(item);
    let group = groups.get(identity.key);
    if (group === undefined) {
      group = { ...identity, items: [] };
      groups.set(identity.key, group);
    }
    group.items.push(item);
  }
  return [...groups.values()].sort(
    (a, b) =>
      groupRank(a) - groupRank(b) ||
      b.items.length - a.items.length ||
      a.policyName.localeCompare(b.policyName),
  );
}

/** The proposal in one line, from whichever field its kind uses. */
export function proposalLine(
  recommendation: Pick<Recommendation, "kind" | "proposedChange">,
): string | null {
  const change = recommendation.proposedChange as Record<string, unknown>;
  const value = (key: string): string | null => {
    const raw = change[key];
    if (typeof raw === "string") return raw;
    if (Array.isArray(raw)) return raw.filter((item) => typeof item === "string").join(", ");
    return null;
  };
  switch (recommendation.kind) {
    case "rename":
      return value("title");
    case "review_cadence":
      return value("reviewFrequency");
    case "owner":
      return value("ownerEmails");
    case "department":
      return value("department");
    case "jurisdictions":
      return value("jurisdictionSlugs");
    case "reclassify":
      return value("documentKind");
    case "framework":
      return value("frameworkName") ?? value("frameworkId");
    case "control_mapping": {
      const action = value("controlAction") === "unlink" ? "Unlink" : "Link";
      const code = value("controlCode") ?? value("controlId");
      return code ? `${action} ${code}` : action;
    }
    case "control_amendment": {
      const code = value("controlCode");
      const section = value("section");
      return [code, section].filter(Boolean).join(" · ") || null;
    }
    // No one-line form, on purpose: the wording *is* the change, and a
    // paragraph squeezed onto the "Change:" line is the half a reader would
    // approve without reading. It renders in full as the proposed wording.
    case "org_context":
      return null;
    default:
      return null;
  }
}
